import React, { useEffect, useState } from 'react';
import { Card, Select, Spin } from 'antd';
import type { SysAdminUserCreate, SysAdminUserUpdate } from '../types/sysadmin-user.types';
import { useSysAdminUserForm } from '../hooks/use-sysadmin-user-form.hook';
import { useRoleList } from '../../sysadmin-roles/hooks/use-role.hook';
import FormBuilder, { FormField } from '../../../../common/FormBuilder';
import { useTranslation } from 'react-i18next';
import { DefaultValues } from 'react-hook-form';
import { ValidationError } from '../../../../../types/api-response.types';
import { Input } from 'antd';
import { Auth0IdentityProvider } from '@gluai-studio/passport';
import SelectField from '@components/common/SelectField';

interface SysAdminUserFormProps {
  defaultValues?: DefaultValues<SysAdminUserCreate | SysAdminUserUpdate>;
  onFormSubmit: (data: SysAdminUserCreate | SysAdminUserUpdate, resetForm: () => void) => void;
  isLoading: boolean;
  columns?: 1 | 2 | 3 | 4;
  validationErrors?: ValidationError[];
}

function SysAdminUserFormComponent({
  defaultValues,
  onFormSubmit,
  isLoading = false,
  columns = 2,
  validationErrors = [],
}: SysAdminUserFormProps) {
  const { t } = useTranslation();
  const [formFields, setFormFields] = useState<
    FormField<SysAdminUserCreate | SysAdminUserUpdate>[]
  >([]);

  const { control, errors, resetForm, formSubmitHandler, reset, setError, setValue } =
    useSysAdminUserForm({
      defaultValues,
      onSubmit: (data) => onFormSubmit(data, resetForm),
    });

  // Reset form values when defaultValues change
  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  // Handle API validation errors
  useEffect(() => {
    validationErrors.forEach((error) => {
      setError(error.path as any, {
        type: 'manual',
        message: error.message,
      });
    });
  }, [validationErrors, setError]);

  // Manage form fields based on defaultValues
  useEffect(() => {
    const roleField: FormField<SysAdminUserCreate | SysAdminUserUpdate> = {
      name: 'role',
      label: t('users.role.label'),
      required: true,
      errorMsg: errors?.role?.message as string | undefined,
      render: (field) => (
        <SelectField
          {...field}
          disabled={isLoading}
          placeholder={t('users.role.placeholder')}
          useListHook={useRoleList}
          labelKey="name"
          valueKey="id"
        />
      ),
    };

    const allFields: FormField<SysAdminUserCreate | SysAdminUserUpdate>[] = [
      {
        name: 'name',
        label: t('users.name.label'),
        required: true,
        errorMsg: errors?.name?.message as string | undefined,
        rules: {
          required: 'Name is required',
          minLength: { value: 3, message: 'Name must be at least 3 characters' },
          maxLength: { value: 50, message: 'Name must be at most 50 characters' },
        },
        render: (field) => (
          <Input {...field} disabled={isLoading} placeholder={t('users.name.placeholder')} />
        ),
      },
      {
        name: 'email',
        label: t('users.email.label'),
        required: true,
        errorMsg: errors?.email?.message as string | undefined,
        rules: { required: 'Email is required' },
        render: (field) => (
          <Input {...field} disabled={isLoading} placeholder={t('users.email.placeholder')} />
        ),
      },
      {
        name: 'userType',
        label: t('users.userType.label'),
        required: true,
        errorMsg: errors?.userType?.message as string | undefined,
        render: (field) => (
          <Select {...field} disabled={isLoading} placeholder={t('users.userType.placeholder')}>
            <Select.Option value={Auth0IdentityProvider.DATABASE}>
              {t('users.userType.database')}
            </Select.Option>
            <Select.Option value={Auth0IdentityProvider.GOOGLE}>
              {t('users.userType.google')}
            </Select.Option>
          </Select>
        ),
      },
      roleField,
    ];

    setFormFields(defaultValues ? [roleField] : allFields);
  }, [defaultValues, isLoading, t, errors]);

  return (
    <Spin spinning={isLoading} tip={t('common.submittingForm')}>
      <Card className="shadow-lg">
        <FormBuilder
          formFields={formFields}
          control={control}
          errors={errors}
          onSubmit={formSubmitHandler()}
          submitButtonText={t('common.save')}
          isLoading={isLoading}
          columns={columns}
        />
      </Card>
    </Spin>
  );
}

// Custom comparison function for memoization
function areEqual(prevProps: SysAdminUserFormProps, nextProps: SysAdminUserFormProps): boolean {
  return (
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.onFormSubmit === nextProps.onFormSubmit &&
    prevProps.columns === nextProps.columns &&
    JSON.stringify(prevProps.defaultValues) === JSON.stringify(nextProps.defaultValues) &&
    JSON.stringify(prevProps.validationErrors) === JSON.stringify(nextProps.validationErrors)
  );
}

export default React.memo(SysAdminUserFormComponent, areEqual);
